import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { SiteHeader, SiteFooter } from '../../components/SiteLayout';
import AgMark from '../../components/brand/AgMark';
import { Upload, Archive, Download, FileText, ArrowsClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SLATE = '#0B1426';
const MUTED = '#5B6577';
const CORAL = '#FF4E6A';
const IVORY = '#F5F1E8';
const HAIRLINE = '#E3D9C0';

/* ====================================================================
 * Asset Center (admin-only) — internal brand/business document library.
 *
 * Scaffold-only per the launch scope: upload/list/update/archive/download.
 * No workflow engine, no analytics, no advanced DAM. A focused place for
 * the team to manage decks, one-pagers, templates, and brand assets.
 * ==================================================================== */

const CATEGORY_LABELS = {
  'investor-deck': 'Investor deck',
  'enterprise-deck': 'Enterprise deck',
  'governance-deck': 'Governance deck',
  'partnership-deck': 'Partnership deck',
  'sales-one-pager': 'Sales one-pager',
  'compliance-passport': 'Compliance passport',
  'scientific-framework': 'Scientific framework',
  'media-kit': 'Media kit',
  'founder-bio': 'Founder bio',
  'outreach-template': 'Outreach template',
  'email-template': 'Email template',
  'retailer-deck': 'Retailer deck',
  'advisory-deck': 'Advisory deck',
};

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

export default function AssetCenterPage() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadAssets = useCallback(async () => {
    try {
      setError('');
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API_URL}/api/admin/asset-center/assets`, {
        params,
        withCredentials: true,
      });
      setAssets(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load assets.');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/asset-center/categories`, { withCredentials: true })
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => {});
    loadAssets();
  }, [loadAssets]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    if (!fd.get('file')) return;
    setUploading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/asset-center/assets`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      form.reset();
      await loadAssets();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleArchive = async (asset) => {
    try {
      const newStatus = asset.status === 'active' ? 'archived' : 'active';
      await axios.patch(
        `${API_URL}/api/admin/asset-center/assets/${asset.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      await loadAssets();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not change status.');
    }
  };

  const handleDownload = async (asset) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/asset-center/assets/${asset.id}/download`,
        { withCredentials: true }
      );
      const { filename, content_type, data_b64 } = res.data;
      const bin = atob(data_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Download failed.');
    }
  };

  if (!user || user.role !== 'admin') {
    return null; // AdminRoute already 404s — defensive guard
  }

  return (
    <>
      <SiteHeader />
      <Helmet>
        <title>Asset Center · ToxIntel Admin</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="min-h-[70vh]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="asset-center-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 pb-6 border-b" style={{ borderColor: HAIRLINE }}>
          <div className="flex items-center gap-4">
            <AgMark size={44} variant="light" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
                Internal · Admin only
              </p>
              <h1 className="font-serif text-3xl mt-1" style={{ color: SLATE }}>
                Asset Center
              </h1>
              <p className="text-[13px] mt-1" style={{ color: MUTED }}>
                Centralized library for decks, one-pagers, templates, and brand assets.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadAssets}
            className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.18em] hover:text-[#FF4E6A] transition-colors"
            style={{ color: MUTED }}
            data-testid="asset-center-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>

        {/* Upload form */}
        <form
          onSubmit={handleUpload}
          className="mb-10 p-6 border rounded-sm"
          style={{ borderColor: HAIRLINE, backgroundColor: IVORY }}
          data-testid="asset-center-upload-form"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: SLATE }}>
            Upload new asset
          </p>
          <div className="grid md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-3">
              <label className="block text-[11px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: MUTED }}>
                Category
              </label>
              <select
                name="category"
                required
                className="w-full border bg-white rounded-sm px-3 py-2 text-sm"
                style={{ borderColor: HAIRLINE }}
                data-testid="asset-center-category-select"
              >
                <option value="">Select…</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-[11px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: MUTED }}>
                Title
              </label>
              <input
                name="title"
                required
                type="text"
                placeholder="e.g. 2026 Investor Deck v3"
                className="w-full border bg-white rounded-sm px-3 py-2 text-sm"
                style={{ borderColor: HAIRLINE }}
                data-testid="asset-center-title-input"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[11px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: MUTED }}>
                File (≤ 25 MB)
              </label>
              <input
                name="file"
                required
                type="file"
                className="w-full text-sm"
                data-testid="asset-center-file-input"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={uploading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-[12px] font-medium uppercase tracking-wider shadow-sm transition-transform hover:-translate-y-px disabled:opacity-50"
                style={{ backgroundColor: SLATE, color: IVORY }}
                data-testid="asset-center-upload-submit"
              >
                <Upload size={14} weight="bold" /> {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
            <div className="md:col-span-12">
              <input
                name="notes"
                type="text"
                placeholder="Internal notes (optional)"
                className="w-full border bg-white rounded-sm px-3 py-2 text-sm"
                style={{ borderColor: HAIRLINE }}
                data-testid="asset-center-notes-input"
              />
            </div>
          </div>
        </form>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border bg-white rounded-sm px-3 py-1.5 text-sm"
            style={{ borderColor: HAIRLINE }}
            data-testid="asset-center-filter-category"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border bg-white rounded-sm px-3 py-1.5 text-sm"
            style={{ borderColor: HAIRLINE }}
            data-testid="asset-center-filter-status"
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="">All</option>
          </select>
        </div>

        {/* Error */}
        {error && (
          <div
            className="mb-4 p-3 text-sm rounded-sm border"
            style={{ borderColor: '#F5A5B0', backgroundColor: '#FFF1F4', color: '#8B2230' }}
            data-testid="asset-center-error"
          >
            {error}
          </div>
        )}

        {/* List */}
        {loading ? (
          <p className="text-sm" style={{ color: MUTED }}>Loading assets…</p>
        ) : assets.length === 0 ? (
          <div
            className="border rounded-sm p-12 text-center"
            style={{ borderColor: HAIRLINE, borderStyle: 'dashed' }}
            data-testid="asset-center-empty"
          >
            <FileText size={32} color={MUTED} weight="duotone" className="mx-auto mb-3" />
            <p className="text-sm" style={{ color: MUTED }}>
              No assets in this view. Upload the first one above.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="asset-center-list">
            {assets.map((a) => (
              <li
                key={a.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-3"
                style={{ borderColor: HAIRLINE }}
                data-testid={`asset-row-${a.id}`}
              >
                <FileText size={20} color={SLATE} weight="duotone" />
                <div className="flex-1 min-w-[200px]">
                  <p className="font-serif text-base" style={{ color: SLATE }}>{a.title}</p>
                  <p className="text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: MUTED }}>
                    {CATEGORY_LABELS[a.category] || a.category} · {formatBytes(a.size_bytes)} · {formatDate(a.uploaded_at)} · {a.uploaded_by}
                  </p>
                  {a.notes && <p className="text-[12px] mt-1" style={{ color: MUTED }}>{a.notes}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => handleDownload(a)}
                  className="text-[11px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:text-[#FF4E6A] transition-colors"
                  style={{ color: SLATE }}
                  data-testid={`asset-download-${a.id}`}
                >
                  <Download size={13} weight="bold" /> Download
                </button>
                <button
                  type="button"
                  onClick={() => handleArchive(a)}
                  className="text-[11px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:text-[#FF4E6A] transition-colors"
                  style={{ color: MUTED }}
                  data-testid={`asset-archive-${a.id}`}
                >
                  <Archive size={13} weight="bold" /> {a.status === 'active' ? 'Archive' : 'Unarchive'}
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
